import { createRef, useEffect, ChangeEvent } from "react";
import { Grid, MenuItem } from "@mui/material";
import { useContextContrato } from "src/Contexts/Lancamentos/Contrato/ContratosContext";
import { ICreateNovaParcelaBordero } from "src/Contexts/Lancamentos/Contrato/ContratosTypes";
import { AppTextField } from "src/Pages/components";
import { FormatMoney, handleFormatPercent } from "src/shared/Utils/FormatMoney";
import { CardAlerts } from "src/Pages/components/CardAlerts/CardAlerts";
import { InfoOutlinedIcon } from "src/Pages/components/Icons/Icons";
import { DividerCore } from "src/Pages/components/DividerCore/DividerCore";

export const FormNovaParcela = () => {
  const { valuesInputNovaParcelaBordero, setValuesInputNovaParcelaBordero } =
    useContextContrato();

  const refOrigem = createRef<HTMLInputElement>();
  const handleFocusedID = () => refOrigem.current?.focus();

  useEffect(() => {
    handleFocusedID();
  }, []);

  const handleChange =
    (
      prop: keyof ICreateNovaParcelaBordero,
      option?: "limitNumber" | "money" | "percent"
    ) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        if (option === "money") {
          setValuesInputNovaParcelaBordero({
            ...valuesInputNovaParcelaBordero!,
            [prop]: FormatMoney(event.target.value),
          });
        } else if (option === "limitNumber") {
          if (event.target.value.length <= 5) {
            setValuesInputNovaParcelaBordero({
              ...valuesInputNovaParcelaBordero!,
              [prop]: event.target.value,
            });
          }
        } else if (option === "percent") {
          if (event.target.value.length <= 7) {
            setValuesInputNovaParcelaBordero({
              ...valuesInputNovaParcelaBordero!,
              [prop]: handleFormatPercent(event.target.value),
            });
          }
        } else {
          setValuesInputNovaParcelaBordero({
            ...valuesInputNovaParcelaBordero!,
            [prop]: event.target.value,
          });
        }
      };

  return (
    <>
      {valuesInputNovaParcelaBordero && (
        <Grid container spacing={2} sx={{ marginBottom: "1.5rem" }}>
          <Grid container item spacing={2}>
            <Grid item xs={8}>
              <AppTextField
                fullWidth
                label="Proposta"
                sxStyle={{ opacity: ".5", pointerEvents: "none", margin: 0 }}
                value={valuesInputNovaParcelaBordero?.cod_proposta}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={4}>
              <AppTextField
                fullWidth
                label="Parcela *"
                inputRef={refOrigem}
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={valuesInputNovaParcelaBordero.numero_parcela}
                variant="outlined"
                type="number"
                onChange={handleChange("numero_parcela")}
              />
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Vencimento *"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                type="date"
                value={valuesInputNovaParcelaBordero?.data_vencimento_parcela}
                variant="outlined"
                onChange={handleChange("data_vencimento_parcela")}
              />
            </Grid>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Vidas *"
                type="number"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={valuesInputNovaParcelaBordero?.contratos_vidas}
                variant="outlined"
                onChange={handleChange("contratos_vidas")}
              />
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <AppTextField
                fullWidth
                label="Mensalidade *"
                sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                value={valuesInputNovaParcelaBordero?.contratos_mensalidade}
                variant="outlined"
                onChange={handleChange("contratos_mensalidade", "money")}
              />
            </Grid>

            <Grid container item spacing={2}>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Escritório *"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={
                    valuesInputNovaParcelaBordero?.porcentagem_comissao_corretora
                  }
                  variant="outlined"
                  onChange={handleChange(
                    "porcentagem_comissao_corretora",
                    "percent"
                  )}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Corretor *"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={
                    valuesInputNovaParcelaBordero?.porcentagem_comissao_corretor
                  }
                  variant="outlined"
                  onChange={handleChange(
                    "porcentagem_comissao_corretor",
                    "percent"
                  )}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Supervisor"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={valuesInputNovaParcelaBordero?.porcentagem_comissao_supervisor}
                  variant="outlined"
                  onChange={handleChange("porcentagem_comissao_supervisor", "percent"
                  )}
                />
              </Grid>
              <Grid item sm={3} xs={6}>
                <AppTextField
                  fullWidth
                  label="% Gerente"
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                  value={valuesInputNovaParcelaBordero?.porcentagem_comissao_gerente}
                  variant="outlined"
                  onChange={handleChange("porcentagem_comissao_gerente", "percent")}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <DividerCore title="Vitalício" sxStyle={{ margin: "0" }} />
            </Grid>

            {/* Vitalício — alinhado ao formulário de editar parcela */}
            <Grid container item spacing={1}>
              <Grid item xs={3}>
                <AppTextField
                  fullWidth
                  label="Vitalício Corretora"
                  variant="outlined"
                  select
                  value={valuesInputNovaParcelaBordero?.is_vitalicio_corretora ? "TRUE" : "FALSE"}
                  onChange={(e) =>
                    setValuesInputNovaParcelaBordero({
                      ...valuesInputNovaParcelaBordero!,
                      is_vitalicio_corretora: e.target.value === "TRUE",
                    })
                  }
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                >
                  <MenuItem value="TRUE">Sim</MenuItem>
                  <MenuItem value="FALSE">Não</MenuItem>
                </AppTextField>
              </Grid>
              <Grid item xs={3}>
                <AppTextField
                  fullWidth
                  label="Vitalício Corretor"
                  variant="outlined"
                  select
                  value={valuesInputNovaParcelaBordero?.is_vitalicio_corretor ? "TRUE" : "FALSE"}
                  onChange={(e) =>
                    setValuesInputNovaParcelaBordero({
                      ...valuesInputNovaParcelaBordero!,
                      is_vitalicio_corretor: e.target.value === "TRUE",
                    })
                  }
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                >
                  <MenuItem value="TRUE">Sim</MenuItem>
                  <MenuItem value="FALSE">Não</MenuItem>
                </AppTextField>
              </Grid>
              <Grid item xs={3}>
                <AppTextField
                  fullWidth
                  label="Vitalício Supervisor"
                  variant="outlined"
                  select
                  value={valuesInputNovaParcelaBordero?.is_vitalicio_supervisor ? "TRUE" : "FALSE"}
                  onChange={(e) =>
                    setValuesInputNovaParcelaBordero({
                      ...valuesInputNovaParcelaBordero!,
                      is_vitalicio_supervisor: e.target.value === "TRUE",
                    })
                  }
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                >
                  <MenuItem value="TRUE">Sim</MenuItem>
                  <MenuItem value="FALSE">Não</MenuItem>
                </AppTextField>
              </Grid>
              <Grid item xs={3}>
                <AppTextField
                  fullWidth
                  label="Vitalício Gerente"
                  variant="outlined"
                  select
                  value={valuesInputNovaParcelaBordero?.is_vitalicio_gerente ? "TRUE" : "FALSE"}
                  onChange={(e) =>
                    setValuesInputNovaParcelaBordero({
                      ...valuesInputNovaParcelaBordero!,
                      is_vitalicio_gerente: e.target.value === "TRUE",
                    })
                  }
                  sxStyle={{ opacity: "", pointerEvents: "auto", margin: 0 }}
                >
                  <MenuItem value="TRUE">Sim</MenuItem>
                  <MenuItem value="FALSE">Não</MenuItem>
                </AppTextField>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {valuesInputNovaParcelaBordero &&
        String(valuesInputNovaParcelaBordero?.numero_parcela) === "0" && (
        <CardAlerts
          title="Parcela numero 0 é considerada como parcela de adesão !"
          subTitle="Ela é uma parcela normal - Todas as ações estão habilitadas, incluindo edição e geração de comissão !"
          color="primary"
          icon={<InfoOutlinedIcon fontSize="small" />}
        />
      )}
    </>
  );
};
